import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Download,
  ExternalLink,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Maximize2,
  Calendar,
  User,
  FileText,
  Clock,
  Trash2,
  UploadCloud,
  ChevronDown,
} from 'lucide-react';
import { isAdmin } from '@vns/core';
import { useSession } from '../lib/session';
import {
  DRAWING_CATEGORIES,
  type DrawingCategory,
  type ProjectDrawingRow,
  useDeleteDrawing,
  formatFileSize,
  convertToBlobUrl,
  triggerFileDownload,
  openDrawingInNewTab,
} from '../lib/project-drawings';
import { formatDate, useToast } from './ui';
import Confirm from './Confirm';

interface Props {
  initialDrawing: ProjectDrawingRow;
  allVersions?: ProjectDrawingRow[];
  projectName?: string;
  onClose: () => void;
  onUploadNewRevision?: (category: DrawingCategory) => void;
}

export default function DrawingViewerModal({
  initialDrawing,
  allVersions = [],
  projectName,
  onClose,
  onUploadNewRevision,
}: Props) {
  const actor = useSession((s) => s.actor);
  const admin = isAdmin(actor);
  const toast = useToast();

  const [currentDrawing, setCurrentDrawing] = useState<ProjectDrawingRow>(initialDrawing);
  const [zoom, setZoom] = useState<number>(1);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const deleteMutation = useDeleteDrawing(currentDrawing.projectId);

  // Available versions for this drawing category
  const versions = (allVersions.length > 0
    ? allVersions
    : [currentDrawing]
  ).filter((d) => d.category === currentDrawing.category)
   .sort((a, b) => b.version - a.version);

  const categoryMeta = DRAWING_CATEGORIES[currentDrawing.category];
  const isImage =
    currentDrawing.fileType.startsWith('image/') ||
    currentDrawing.fileUrl.startsWith('data:image/') ||
    /\.(png|jpe?g|webp|gif|svg)$/i.test(currentDrawing.fileName);
  const isPdf =
    currentDrawing.fileType === 'application/pdf' ||
    currentDrawing.fileUrl.startsWith('data:application/pdf') ||
    /\.pdf$/i.test(currentDrawing.fileName);

  // Reset zoom on drawing change
  useEffect(() => {
    setZoom(1);
  }, [currentDrawing.id]);

  // Handle ESC key to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !confirmDelete) onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, confirmDelete]);

  function handleZoomIn() {
    setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)));
  }

  function handleZoomOut() {
    setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)));
  }

  function handleResetZoom() {
    setZoom(1);
  }

  const viewerUrl = useMemo(() => {
    if (!currentDrawing.fileUrl) return '';
    if (currentDrawing.fileUrl.startsWith('data:')) {
      return convertToBlobUrl(currentDrawing.fileUrl, currentDrawing.fileType, currentDrawing.fileName);
    }
    return currentDrawing.fileUrl;
  }, [currentDrawing.fileUrl, currentDrawing.fileType, currentDrawing.fileName]);

  function handleDownload() {
    triggerFileDownload(
      currentDrawing.fileUrl,
      currentDrawing.fileName || `${categoryMeta.label}_v${currentDrawing.version}`,
      currentDrawing.fileType,
    );
  }

  function handleOpenNewTab() {
    openDrawingInNewTab(
      currentDrawing.fileUrl,
      currentDrawing.fileName,
      currentDrawing.fileType,
    );
  }

  async function doDeleteRevision() {
    try {
      await deleteMutation.mutateAsync({
        id: currentDrawing.id,
        category: currentDrawing.category,
        targetProjectId: currentDrawing.projectId,
      });
      toast.show(`Revision v${currentDrawing.version} removed`);
      setConfirmDelete(false);
      onClose();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not delete drawing', true);
    }
  }

  return createPortal(
    <>
      <div
        className="scrim"
        onClick={onClose}
        style={{
          background: 'rgba(15, 23, 42, 0.75)',
          backdropFilter: 'blur(4px)',
          zIndex: 9999,
        }}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={currentDrawing.title}
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '94vw',
          maxWidth: 1100,
          maxHeight: '92vh',
          background: 'var(--surface)',
          borderRadius: 'var(--radius)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
          border: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 10000,
          overflow: 'hidden',
        }}
      >
        {/* Header Bar */}
        <div
          style={{
            padding: '14px 20px',
            background: 'var(--surface-2)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '3px 10px',
                borderRadius: 'var(--radius-xs)',
                fontSize: 12,
                fontWeight: 700,
                background: categoryMeta.bg,
                color: categoryMeta.color,
                border: `1px solid ${categoryMeta.borderColor}`,
              }}
            >
              {categoryMeta.label}
            </span>

            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 15,
                  color: 'var(--text)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 420,
                }}
              >
                {currentDrawing.title}
              </div>
              {projectName && (
                <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
                  {projectName}
                </div>
              )}
            </div>
          </div>

          {/* Center / Right controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Version dropdown if multiple versions exist */}
            {versions.length > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, color: 'var(--text-soft)', fontWeight: 500 }}>
                  Revision:
                </span>
                <select
                  value={currentDrawing.id}
                  onChange={(e) => {
                    const found = versions.find((v) => v.id === e.target.value);
                    if (found) setCurrentDrawing(found);
                  }}
                  style={{
                    padding: '4px 8px',
                    fontSize: 12.5,
                    fontWeight: 600,
                    borderRadius: 'var(--radius-xs)',
                    border: '1px solid var(--border)',
                    background: 'var(--surface)',
                    color: 'var(--text)',
                    cursor: 'pointer',
                  }}
                >
                  {versions.map((v) => (
                    <option key={v.id} value={v.id}>
                      v{v.version} {v.isLatest ? '(Latest Active)' : `(${formatDate(v.createdAt)})`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Image Zoom Toolbar */}
            {isImage && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-xs)',
                  padding: '2px 4px',
                  gap: 2,
                }}
              >
                <button
                  type="button"
                  className="icon-btn"
                  onClick={handleZoomOut}
                  title="Zoom Out"
                  style={{ width: 26, height: 26 }}
                >
                  <ZoomOut size={13} />
                </button>
                <span
                  style={{
                    fontSize: 11.5,
                    fontWeight: 600,
                    padding: '0 4px',
                    minWidth: 42,
                    textAlign: 'center',
                    color: 'var(--text-soft)',
                  }}
                >
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  type="button"
                  className="icon-btn"
                  onClick={handleZoomIn}
                  title="Zoom In"
                  style={{ width: 26, height: 26 }}
                >
                  <ZoomIn size={13} />
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  onClick={handleResetZoom}
                  title="Reset Zoom (100%)"
                  style={{ width: 26, height: 26 }}
                >
                  <RotateCcw size={12} />
                </button>
              </div>
            )}

            <button
              type="button"
              className="icon-btn"
              onClick={handleOpenNewTab}
              title="Open in new window"
              style={{ width: 30, height: 30 }}
            >
              <ExternalLink size={14} />
            </button>

            <button
              type="button"
              className="icon-btn"
              onClick={handleDownload}
              title="Download file"
              style={{ width: 30, height: 30 }}
            >
              <Download size={14} />
            </button>

            <button
              type="button"
              className="icon-btn"
              onClick={onClose}
              title="Close modal"
              style={{ width: 30, height: 30, marginLeft: 4 }}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Content Viewer Body */}
        <div
          style={{
            flex: 1,
            minHeight: 380,
            maxHeight: 'calc(92vh - 140px)',
            overflow: 'auto',
            background: isImage ? '#090d16' : 'var(--surface-2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            padding: isImage ? 16 : 0,
          }}
        >
          {isImage ? (
            <div
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: 'center center',
                transition: 'transform 0.15s ease-out',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                maxWidth: '100%',
              }}
            >
              <img
                src={viewerUrl || currentDrawing.fileUrl}
                alt={currentDrawing.title}
                style={{
                  maxWidth: '100%',
                  maxHeight: '72vh',
                  objectFit: 'contain',
                  borderRadius: 4,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                }}
              />
            </div>
          ) : isPdf ? (
            <iframe
              src={viewerUrl || currentDrawing.fileUrl}
              title={currentDrawing.title}
              style={{
                width: '100%',
                height: '70vh',
                border: 'none',
                background: 'var(--surface)',
              }}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <FileText size={48} style={{ color: 'var(--text-soft)', marginBottom: 12 }} />
              <div style={{ fontWeight: 600, fontSize: 16 }}>{currentDrawing.fileName}</div>
              <div style={{ fontSize: 13, color: 'var(--text-soft)', marginTop: 4, marginBottom: 16 }}>
                {formatFileSize(currentDrawing.fileSize)}
              </div>
              <button className="btn btn-primary btn-auto btn-sm" onClick={handleDownload}>
                <Download size={14} />
                <span>Download Document</span>
              </button>
            </div>
          )}
        </div>

        {/* Footer Meta & Actions */}
        <div
          style={{
            padding: '12px 20px',
            background: 'var(--surface)',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
            fontSize: 12.5,
          }}
        >
          {/* Metadata */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              flexWrap: 'wrap',
              color: 'var(--text-soft)',
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontWeight: 600,
                color: currentDrawing.isLatest ? '#059669' : 'var(--text-soft)',
                background: currentDrawing.isLatest ? '#ecfdf5' : 'var(--surface-2)',
                padding: '2px 8px',
                borderRadius: 'var(--radius-xs)',
                border: `1px solid ${currentDrawing.isLatest ? '#a7f3d0' : 'var(--border)'}`,
              }}
            >
              Revision v{currentDrawing.version} {currentDrawing.isLatest && '• Latest Active'}
            </span>

            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Calendar size={13} style={{ color: 'var(--text-faint)' }} />
              <span>{formatDate(currentDrawing.createdAt)}</span>
            </span>

            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <FileText size={13} style={{ color: 'var(--text-faint)' }} />
              <span>{formatFileSize(currentDrawing.fileSize)}</span>
            </span>

            {currentDrawing.uploadedByName && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <User size={13} style={{ color: 'var(--text-faint)' }} />
                <span>Uploaded by {currentDrawing.uploadedByName}</span>
              </span>
            )}

            {currentDrawing.description && (
              <span
                style={{
                  background: 'var(--surface-2)',
                  padding: '2px 8px',
                  borderRadius: 'var(--radius-xs)',
                  color: 'var(--text)',
                  maxWidth: 360,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={currentDrawing.description}
              >
                Note: {currentDrawing.description}
              </span>
            )}
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {admin && (
              <button
                type="button"
                className="btn btn-auto btn-sm btn-ghost btn-danger"
                onClick={() => setConfirmDelete(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 5 }}
              >
                <Trash2 size={13} />
                <span>Delete Revision</span>
              </button>
            )}

            {onUploadNewRevision && (
              <button
                type="button"
                className="btn btn-auto btn-sm btn-primary"
                onClick={() => {
                  onClose();
                  onUploadNewRevision(currentDrawing.category);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 5 }}
              >
                <UploadCloud size={13} />
                <span>+ Upload New Revision</span>
              </button>
            )}

            <button type="button" className="btn btn-auto btn-sm" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>

      {confirmDelete && (
        <Confirm
          title={`Delete Revision v${currentDrawing.version} from Database?`}
          message="This revision file will be deleted from the database. If this is the active version, the most recent previous revision will automatically become the active drawing."
          busy={deleteMutation.isPending}
          onConfirm={() => void doDeleteRevision()}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </>,
    document.body
  );
}
